/**
 * The three Status figures from the Iguana Simulator that no endpoint returns.
 *
 * Timeouts, Disconnections and Bad Messages are not counters on the API. Each is
 * a filter over the message list, which is why `reference-ui-to-api-map.md`
 * lists them as "derive" rather than pointing at a field.
 *
 * Everything here matches on `reason`, a prose string produced in another repo
 * (`packages/mllp-harness/src/channels/outbound.ts`). That makes these functions
 * the fragile seam in this feature: if Paul rewords a message, the counters
 * quietly read zero and nothing else notices. Hence the constants, and hence the
 * test.
 *
 * Scope, which the UI has to state and not imply away: these count a list, and
 * the list is a bounded ring the operator can clear and filter. They are not
 * lifetime counters the way Iguana's are.
 */
import type { Hl7MessageSummary } from './types'

/** `timed out after ${timeoutMs}ms waiting for an acknowledgment` (outbound.ts:81). */
export const TIMEOUT_REASON_PREFIX = 'timed out after'

/** Exact, not a prefix: the sender emits this string whole (outbound.ts:112). */
export const DISCONNECT_REASON = 'peer closed the connection without acknowledging'

/**
 * The other half of "the peer went away", and the reason this is not a one-line
 * equality check.
 *
 * `DISCONNECT_REASON` is only emitted on a clean FIN. When a peer resets instead,
 * the socket `error` handler fires first (outbound.ts:111) and `finish` records
 * the raw Node message, so the reason reads `read ECONNRESET` and never mentions
 * disconnecting at all. Verified both ways against a live harness: draining the
 * frame then closing gives the documented string; closing with data unread gives
 * `read ECONNRESET`.
 *
 * That distinction is not academic for MedStar. Traffic crosses an IPsec tunnel
 * through VNS3 and NAT, and a tunnel blip, a firewall idle-reap or an abrupt peer
 * restart all produce a reset, not a polite FIN. A counter matching only the
 * documented string would read zero during exactly the failure it exists to show.
 *
 * Matched on the errno alone because Node prefixes it with the failing syscall
 * (`read `, `write `, `connect `).
 */
const SOCKET_ERROR_CODES = [
  'ECONNRESET',
  'EPIPE',
  'ECONNREFUSED',
  'ECONNABORTED',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'ETIMEDOUT',
] as const

/**
 * A send that got no acknowledgment in time.
 *
 * Distinct from a peer that answered AE/AR (`peer returned AE`) and from one that
 * echoed the wrong control id. Those are rejections, not transport failures, and
 * Iguana counts them separately too.
 */
export function countTimeouts(messages: Hl7MessageSummary[]): number {
  return messages.filter(
    (message) =>
      message.direction === 'out' &&
      message.ok === false &&
      (message.reason ?? '').startsWith(TIMEOUT_REASON_PREFIX),
  ).length
}

/**
 * A peer that went away rather than staying silent until the timeout.
 *
 * Deliberately broader than `reference-ui-to-api-map.md`, which lists only the
 * clean-close string. See `SOCKET_ERROR_CODES` for why: half of real
 * disconnections never produce that string.
 *
 * `ETIMEDOUT` here is a TCP-level connect timeout, which is a different failure
 * from the MLLP acknowledgment timeout counted by `countTimeouts`. Never reached
 * the peer, versus reached it and got no answer.
 */
export function countDisconnections(messages: Hl7MessageSummary[]): number {
  return messages.filter((message) => {
    if (message.direction !== 'out') return false
    const reason = message.reason ?? ''
    return reason === DISCONNECT_REASON || SOCKET_ERROR_CODES.some((code) => reason.includes(code))
  }).length
}

/**
 * Arrivals we could not parse. `msh` is null and `parseError` says why.
 *
 * Inbound only, and that is a property of the system rather than a shortcut: an
 * outbound message that will not parse is rejected with 400 before anything is
 * dialled, so it never becomes a record. The direction guard keeps that true if
 * that ever changes.
 */
export function countBadMessages(messages: Hl7MessageSummary[]): number {
  return messages.filter((message) => message.direction === 'in' && message.msh === null).length
}
